import React, { useContext, useState } from 'react';
import { Box, Typography, RadioGroup, FormControlLabel, Radio, Paper, Alert, IconButton, Tooltip, Chip, Button, CircularProgress, Checkbox, Switch } from '@mui/material';
import axios, { AxiosResponse } from 'axios';
import { API_ENDPOINT } from '../../../../configurations/global.config';
import { AuthContext } from '../../../../contexts/user-context/user-context';
import { useSnackBar } from '../../../../contexts/snackbar';
import { AutoMode, InfoOutlined, Lightbulb, LooksOne, Settings, SwipeRight } from '@mui/icons-material';
import { IconWithText } from '../../../common/styles/components';
import Grid2 from '@mui/material/Unstable_Grid2';
import AutocompleteWithThrottle from '../../../common/autocomplete-with-throttle/autocomplete-with-throttle';
import { ShopLocationType } from '../../../../interfaces/delivery-company/pickups';
import { displayLocation } from '../../../../utils/locations';
import { ErrorsFlattenner } from '../../../common/errors-flattenner/errors-flattenner';
import { GiCrackedGlass } from 'react-icons/gi';
import { OrgSettingsContext } from '../../../../contexts/org-settings-context';
import { WoocommerceIntegrationConfig } from '../../../../interfaces/common/woocommerce';
import { WoocommercePackagingMode, WoocommerceSyncMode } from '../../../../enums/woocommerce';

interface WoocommerceIntegrationConfigFormProps {
  value: WoocommerceIntegrationConfig;
  updateValue: (value: WoocommerceIntegrationConfig) => void;
  storeId: number | null;
}

const areEqual = (a: WoocommerceIntegrationConfig, b: WoocommerceIntegrationConfig) => {
  return a.sync_mode == b.sync_mode && a.packaging_mode == b.packaging_mode && a.merchant_location?.id == b.merchant_location?.id && a.default_is_fragile == b.default_is_fragile;
}

const isValid = (config: WoocommerceIntegrationConfig) => {
  return config.sync_mode !== null && config.packaging_mode !== null;
}

function WoocommerceIntegrationConfigForm ({ value,storeId,updateValue}:WoocommerceIntegrationConfigFormProps)  {
  const [isUpdatingConfig, setIsUpdatingConfig] = useState(false);
  const { config } = useContext(AuthContext);
  const [integrationConfig, setIntegrationConfig] = useState<WoocommerceIntegrationConfig>(value);
  const [response, setResponse] = useState<AxiosResponse>()
  const [message, setMessage] = useState('');
  const { showMessage } = useSnackBar();
  const { color } = useContext(OrgSettingsContext)
  

  const handleSaveConfig = async () => {
    setIsUpdatingConfig(true);
    setMessage('');
    setResponse(undefined);
    try {
      const response = await axios.put(
        `${API_ENDPOINT}/woocommerce/v1/woocommerce-store/${storeId}/`,
        integrationConfig,
        config
      );
      updateValue(integrationConfig);
      setResponse(response);
      setMessage('Integration settings updated!');
      showMessage('Integration settings updated!', 'success');
    } catch(e) {
      if (axios.isAxiosError(e)) {
        setResponse(e.response);
        setMessage('Failed to update configuration settings: ' + e.response?.statusText);
      } else {
        setMessage('Failed to update configuration settings');
      }
      console.log(e);
      showMessage('Failed to update configuration settings', 'error');
    } finally {
      setIsUpdatingConfig(false);
    }
  };

  const handleFetchPickupLocations = async (query: string) => {

      const endpoint = `${API_ENDPOINT}/api/v1/pickups/?page_size=10&name__contains=${encodeURIComponent(query)}`;

      try {
        const response = await axios.get<{
          count: number;
          results: ShopLocationType[];
        }>(endpoint, config);

        console.log(response);
        return response.data.results;
      } catch (error) {
        console.log(error);
      }
    
    return [];
  };

  const valueChanged = !areEqual(value, integrationConfig);
  console.log("value changed = ", valueChanged)

  return (
    <Box mt={2}>
      <>
      <IconWithText centered>
        <Settings color="primary" fontSize={'large'}  />
       <Typography fontWeight={600} color={"primary"}> Integration Setting</Typography>
      </IconWithText>

      {valueChanged && isValid(integrationConfig) && <Box mt={2} justifyContent="center" width="100%" display="flex">
            {isUpdatingConfig ? <CircularProgress size={32} color="primary"/> : <Button size="small" variant="contained" color="primary" onClick={handleSaveConfig}> Save Changes</Button>}
        </Box>}


        {message != '' && <>
          <Alert
            severity={
              response && response.status >= 200 && response.status < 300
                ? 'success'
                : 'error'
            }
            sx={{mt: 2}}
            onClose={() => {setMessage(''); setResponse(undefined)}}
          >
            <>
              {message}

              {response && response.status >= 300 && (
                <ErrorsFlattenner rawErrors={response.data} />
              )}
            </>
          </Alert>
          <br />
        </>}


      {/* Sync Mode */}
      <Box mt={2}>
        <Typography fontWeight={600} color={"primary"}> Sync Mode</Typography>
        <Typography variant="body2" color={"text.secondary"}> Decide if orders should be imported automatically or manually</Typography>
        <Grid2 spacing={0} container mt={1}>
          <Grid2  md={6} display="flex" justifyContent="center">  
              <Chip 
                  onClick={() => setIntegrationConfig({...integrationConfig, sync_mode: WoocommerceSyncMode.AUTOMATIC})}
                  color={integrationConfig.sync_mode === WoocommerceSyncMode.AUTOMATIC ? "primary" : "default"}
                  disabled={isUpdatingConfig}
                  label={<Tooltip title="Every new woocommerce order will be imported into parcel tracer automatically.">
                    <span>Automatic</span>
                  </Tooltip>} 
                  icon={<AutoMode />} 
                />
          </Grid2>
            
          <Grid2  md={6} display="flex" justifyContent="center">  
                <Chip 
                  onClick={() => setIntegrationConfig({...integrationConfig, sync_mode: WoocommerceSyncMode.MANUAL})}
                  color={integrationConfig.sync_mode === WoocommerceSyncMode.MANUAL ? "primary" : "default"}
                  disabled={isUpdatingConfig}
                  label={<Tooltip title="Woocommerce orders will only be imported into parcel tracer when you manually click import.">
                    <span>Manual</span>
                  </Tooltip>} 
                  icon={<SwipeRight />} 
                />
            </Grid2>
        </Grid2>
      </Box>

      {/* Packaging Mode*/}
      <Box mt={3}>
        <Typography fontWeight={600} color={"primary"}> Packaging Mode 
        </Typography>
        <Typography variant="body2" color={"text.secondary"}>
          Define how Woocommerce orders should be packaged in parcel tracer.
        </Typography>
        <Grid2 spacing={0} container mt={1}>
          <Grid2  md={6} display="flex" justifyContent="center">  
            <Chip 
              onClick={() => setIntegrationConfig({...integrationConfig, packaging_mode: WoocommercePackagingMode.AUTO})}
              color={integrationConfig.packaging_mode === WoocommercePackagingMode.AUTO ? "primary" : "default"}
              label={<Tooltip title="Package each item in the woocommerce order as a separate package.">
                <span>Automatic</span>
              </Tooltip>} 
              disabled={isUpdatingConfig}
              icon={<AutoMode />} 
            />
            </Grid2>

            <Grid2  md={6} display="flex" justifyContent="center">  
            
            <Chip 
              onClick={() => setIntegrationConfig({...integrationConfig, packaging_mode: WoocommercePackagingMode.ONE_PACKAGE})}
              color={integrationConfig.packaging_mode === WoocommercePackagingMode.ONE_PACKAGE ? "primary" : "default"}
              label={<Tooltip title="Always set the order as a single package, regardless of product quantities.">
                <span>One Package</span>
              </Tooltip>} 
              disabled={isUpdatingConfig}
              icon={<LooksOne />} 
            />
            </Grid2>
        </Grid2>
      </Box>

      <Box mt={3}>
        <Typography fontWeight={600} color={"primary"}> Pickup Location </Typography>
        <Typography sx={{mb: 1}} variant="body2" color={"text.secondary"}>Choose the location where woocommerce orders will be picked up.</Typography>

        <AutocompleteWithThrottle
            label={'Location'}
            fetchAllOnLoad
            value={integrationConfig.merchant_location}
            onChange={(e, value) => setIntegrationConfig({ ...integrationConfig, merchant_location: value })}
            fetch={handleFetchPickupLocations} 
            getOptionLabel={function (option: ShopLocationType): string {
              return `${option.name}: ${displayLocation(option)}`;
            }}
        />


      </Box>

      <Box mt={3}>
        <Typography fontWeight={600} color={"primary"}> Other Settings </Typography>
        
        
        <Box display="flex" alignItems="center" gap={0}>
        <Switch checked={integrationConfig.default_is_fragile} onChange={(e) => setIntegrationConfig({ ...integrationConfig, default_is_fragile: !integrationConfig.default_is_fragile })}/>
          <IconWithText> All packages are fragile by default <GiCrackedGlass size={24} color={color} /></IconWithText>
        </Box>
      </Box>

      </>
    </Box>

  );
}

export default WoocommerceIntegrationConfigForm;

